import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, Loader2, Mic, Send, Square, User } from 'lucide-react'
import MarkdownMessage from './MarkdownMessage'
import ThinkingBlock from './ThinkingBlock'
import { getApiBase, getToken } from '../lib/utils'

export type AgentSummary = {
  id: number
  name: string
  slug: string
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  disable_own_knowledge: boolean
  icon_preview_url?: string | null
}

export type AgentSandboxChatProps = {
  agent: AgentSummary
  conversationId: number | null
  onConversationId: (id: number) => void
  onQuotaBypassNotice?: () => void
}

export type SandboxMessage = {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  pending?: boolean
}

export default function AgentSandboxChat({ agent, conversationId, onConversationId, onQuotaBypassNotice }: AgentSandboxChatProps) {
  const [input, setInput] = useState('')
  const [history, setHistory] = useState<SandboxMessage[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [thinkingText, setThinkingText] = useState('')
  const [isThinking, setIsThinking] = useState(false)

  // Voice dictation state
  const [isListening, setIsListening] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  const recognitionRef = useRef<any>(null)
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 120) + 'px'
    }
  }, [])

  useEffect(() => {
    autoResize()
  }, [input, autoResize])

  // Speech Recognition setup
  const startListening = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      alert('La reconnaissance vocale n\'est pas supportée par votre navigateur. Utilisez Chrome ou Edge.')
      return
    }

    const recognition = new SpeechRecognition()
    recognition.lang = 'fr-FR'
    recognition.continuous = true
    recognition.interimResults = true

    recognition.onstart = () => {
      setIsListening(true)
      setInterimTranscript('')
    }

    recognition.onresult = (event: any) => {
      // Reset silence timer on each result
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current)
      silenceTimeoutRef.current = setTimeout(() => {
        recognition.stop()
      }, 3000)

      let interim = ''
      let final = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript
        } else {
          interim += transcript
        }
      }
      if (final) {
        setInput(prev => {
          const separator = prev && !prev.endsWith(' ') ? ' ' : ''
          return prev + separator + final
        })
        setInterimTranscript('')
      } else {
        setInterimTranscript(interim)
      }
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      if (event.error !== 'aborted') {
        setIsListening(false)
      }
    }

    recognition.onend = () => {
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current)
      setIsListening(false)
      setInterimTranscript('')
    }

    recognitionRef.current = recognition
    recognition.start()
  }, [])

  const stopListening = useCallback(() => {
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current)
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }
    setIsListening(false)
    setInterimTranscript('')
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current)
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
    }
  }, [])

  useEffect(() => {
    setHistory([])
    setInput('')
    setError(null)
  }, [agent.id])

  const handleSubmit = useCallback(async () => {
    if (!input.trim()) return
    const question = input.trim()
    const userMsg: SandboxMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
    }
    setHistory(prev => [...prev, userMsg, { id: `pending-${Date.now()}`, role: 'assistant', content: '', pending: true }])
    setInput('')
    setSending(true)
    setError(null)
    setStatusMessage(null)

    try {
      const token = getToken()
      const resp = await fetch(`${getApiBase()}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question,
          agent_id: agent.id,
          sandbox: true,
          conversation_id: conversationId,
        }),
      })

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}))
        throw new Error(errorData.detail || `HTTP ${resp.status}`)
      }

      const reader = resp.body!.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullAnswer = ''
      let firstTokenReceived = false
      const assistantId = `assistant-${Date.now()}`

      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue

          try {
            const data = JSON.parse(line.slice(6))

            if (data.error) {
              throw new Error(data.error)
            }

            if (data.status_message && !firstTokenReceived) {
              setStatusMessage(data.status_message)
            }

            // Gérer les événements thinking (modèles avec <think>)
            if (data.thinking_started) {
              setIsThinking(true)
              setThinkingText('')
              setStatusMessage('Réflexion en cours…')
            }
            if (data.thinking) {
              setThinkingText(prev => prev + data.thinking)
            }
            if (data.thinking_done) {
              setIsThinking(false)
              setStatusMessage(null)
            }

            if (data.token) {
              if (!firstTokenReceived) {
                firstTokenReceived = true
                setStatusMessage(null)
                fullAnswer = data.token
                setHistory(prev => {
                  const next = [...prev]
                  const pendingIndex = next.findIndex(msg => msg.pending)
                  if (pendingIndex !== -1) {
                    next[pendingIndex] = { id: assistantId, role: 'assistant', content: fullAnswer }
                  }
                  return next
                })
              } else {
                fullAnswer += data.token
                setHistory(prev => {
                  const next = [...prev]
                  const idx = next.findIndex(msg => msg.id === assistantId)
                  if (idx !== -1) {
                    next[idx] = { ...next[idx], content: fullAnswer }
                  }
                  return next
                })
              }
            }

            if (data.done) {
              if (data.conversation_id) {
                onConversationId(data.conversation_id)
              }
              if (onQuotaBypassNotice) {
                onQuotaBypassNotice()
              }
              // Ajouter sources si présentes
              if (Array.isArray(data.sources) && data.sources.length) {
                const srcs = '\n\n**Sources :**\n' + data.sources.map((s: any) => `- ${s.title ?? 'media ' + s.media_id}`).join('\n')
                fullAnswer += srcs
                setHistory(prev => {
                  const next = [...prev]
                  const idx = next.findIndex(msg => msg.id === assistantId)
                  if (idx !== -1) {
                    next[idx] = { ...next[idx], content: fullAnswer }
                  }
                  return next
                })
              }
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes('JSON')) {
              throw parseErr
            }
          }
        }
      }

      // Emit event for onboarding to detect agent response
      window.dispatchEvent(new CustomEvent('onboarding:agent-response'))
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur sandbox'
      setError(detail)
      setHistory(prev => prev.filter(msg => !msg.pending))
    } finally {
      setSending(false)
      setStatusMessage(null)
      setIsThinking(false)
    }
  }, [agent.id, conversationId, input, onConversationId, onQuotaBypassNotice])

  const formattedHistory = useMemo(() => history, [history])

  // Utiliser l'icône de l'agent au lieu du logo de la plateforme
  const agentIcon = agent.icon_preview_url

  return (
    <div className="flex h-full flex-col" data-onboarding="sandbox-chat">
      {/* Chat Messages Container */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 p-6">
        {formattedHistory.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center space-y-8 text-center">
            {/* Animated Agent Icon with Green Glow */}
            <div className="relative">
              <div className="absolute inset-0 animate-pulse rounded-full bg-emerald-500/30 blur-3xl" />
              {agentIcon ? (
                <img 
                  src={agentIcon} 
                  alt={agent.name} 
                  className="relative h-32 w-32 rounded-full border-4 border-emerald-500/50 object-cover shadow-2xl shadow-emerald-500/50" 
                />
              ) : (
                <div className="relative flex h-32 w-32 items-center justify-center rounded-full border-4 border-emerald-500/50 bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-2xl shadow-emerald-500/50">
                  <span className="text-4xl font-bold text-white">{agent.name.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>

            {/* Welcome Text */}
            <div className="max-w-md space-y-4">
              <h2 className="text-2xl font-bold text-white">Testez {agent.name} ici</h2>
              <p className="text-base leading-relaxed text-zinc-400">
                Cet espace vous permet de vérifier l'efficacité de votre agent avant de le mettre en ligne. Envoyez un message pour commencer.
              </p>
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 text-sm text-zinc-500">
                Toutes les conversations ici sont en mode test et n'affecteront pas votre environnement de production.
              </div>
            </div>

            {/* Start Prompt */}
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <ArrowDown className="h-4 w-4" />
              <span>Commencez par envoyer un message</span>
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-6">
            {formattedHistory.map((message, index) => (
              <div
                key={message.id}
                className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {message.role === 'assistant' && (
                  agentIcon ? (
                    <img 
                      src={agentIcon} 
                      alt={agent.name} 
                      className="h-10 w-10 flex-shrink-0 rounded-full object-cover shadow-lg shadow-emerald-500/30 border-2 border-emerald-500/50" 
                    />
                  ) : (
                    <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-500/30">
                      <span className="text-sm font-semibold text-white">{agent.name.charAt(0).toUpperCase()}</span>
                    </div>
                  )
                )}

                <div className={`flex max-w-[70%] flex-col gap-2 ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`rounded-2xl px-5 py-3 shadow-lg ${
                      message.role === 'user'
                        ? 'bg-emerald-600 text-white'
                        : 'border border-zinc-800 bg-zinc-900/80 text-zinc-100'
                    }`}
                  >
                    {message.pending ? (
                      <div className="flex flex-col gap-2">
                        {isThinking && (
                          <ThinkingBlock content={thinkingText} isActive={true} />
                        )}
                        {isThinking && (
                          <div className="flex items-center gap-2 text-sm text-zinc-400">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Réflexion en cours…</span>
                          </div>
                        )}
                        {!isThinking && (
                          <div className="flex items-center gap-2 text-sm text-zinc-400">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>{statusMessage || 'Réponse en cours…'}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <MarkdownMessage content={message.content} className="text-sm leading-relaxed" />
                    )}
                  </div>
                </div>

                {message.role === 'user' && (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-zinc-700 text-white shadow-lg">
                    <User className="h-5 w-5" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="border-t border-zinc-800 bg-red-950/50 px-6 py-3">
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      {/* WhatsApp-style Input Area */}
      <form
        onSubmit={event => {
          event.preventDefault()
          if (!sending && input.trim()) {
            handleSubmit()
          }
        }}
        className="border-t border-zinc-800 bg-zinc-900/90 px-4 py-3"
      >
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          {/* Input container - WhatsApp pill style */}
          <div className="relative flex flex-1 items-end rounded-3xl border border-zinc-700 bg-zinc-800/60">
            {isListening && (
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 rounded-full bg-red-500/90 px-3 py-1 text-xs font-medium text-white shadow-lg backdrop-blur-sm">
                <div className="h-2 w-2 animate-pulse rounded-full bg-white" />
                <span>Écoute en cours…</span>
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={isListening ? input + (interimTranscript ? (input ? ' ' : '') + interimTranscript : '') : input}
              onChange={event => {
                if (!isListening) {
                  setInput(event.target.value)
                }
              }}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  if (!sending && input.trim()) {
                    handleSubmit()
                  }
                }
              }}
              placeholder={isListening ? 'Parlez maintenant…' : 'Message'}
              rows={1}
              className="flex-1 resize-none rounded-3xl bg-transparent py-3 pl-4 pr-2 text-sm text-white placeholder-zinc-500 focus:outline-none"
              style={{ maxHeight: '120px' }}
              disabled={sending || isListening}
              data-onboarding="sandbox-chat-input"
            />

            {/* Mic button inside input (when no text and not sending) */}
            {!input.trim() && !sending && (
              <button
                type="button"
                onClick={isListening ? stopListening : startListening}
                className={`m-1.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition ${
                  isListening
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600'
                    : 'text-zinc-400 hover:bg-zinc-700 hover:text-emerald-400'
                }`}
                title={isListening ? 'Arrêter la dictée' : 'Dictée vocale'}
              >
                {isListening ? (
                  <Square className="h-4 w-4" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            )}
          </div>

          {/* Send button - appears when there's text (WhatsApp style) */}
          {(input.trim() || sending) && (
            <button
              type="submit"
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={sending || !input.trim()}
              title="Envoyer (Entrée)"
            >
              {sending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
